// OPS Occult Archive v22.3.54 - balanced B-Z editorial batch.
// Public copy is original OPS writing. Historical source discovery notes remain private.
(()=>{
  const pack=window.OPS_OCCULT_EDITORIAL||(window.OPS_OCCULT_EDITORIAL={version:'22.3.54',entries:[]});
  const incoming=[
  {
    "title": "Baphomet",
    "slug": "baphomet",
    "type": "Occult image, accusation & later esoteric emblem",
    "depth": "Shallows",
    "synopsis": "A name from the accusations against the Templars that later became attached to one of occultism's most recognisable goat-headed images.",
    "lead": [
      "Baphomet is a good example of an occult image becoming far more definite with time. Medieval accusations against the Knights Templar mention an idol or mysterious head under versions of the name, but the evidence is contradictory and bound up with a hostile prosecution. The familiar horned, winged, torch-bearing figure came much later.",
      "That modern visual vocabulary is chiefly associated with the nineteenth-century occultist Éliphas Lévi. His figure deliberately combines opposites: animal and human, male and female, above and below, light and darkness. It is a diagram in the shape of a creature, which is why it has proved so endlessly reusable in occult art."
    ],
    "sections": [
      {
        "heading": "The medieval name is not the modern picture",
        "paragraphs": [
          "The Templar trials produced several descriptions of alleged heads or idols, but there is no single surviving medieval Baphomet image that matches the modern goat figure. Older occult encyclopedias often repeat confident explanations that historians now treat much more cautiously.",
          "The uncertainty is part of the subject. Baphomet begins as an accusation, a name and a cluster of rumours before becoming a stable visual emblem centuries later."
        ]
      },
      {
        "heading": "Lévi's symbolic creature",
        "paragraphs": [
          "Lévi's Baphomet is built from deliberate contrasts: a torch between the horns, one hand raised and one lowered, lunar crescents, wings, hooves and human features. The image is less a portrait of a particular being than a symbolic composition about equilibrium and reconciliation of opposites.",
          "That makes it especially useful as an art reference. Every element can be isolated and studied without reducing the whole subject to 'goat equals devil'."
        ]
      },
      {
        "heading": "Why the image keeps changing",
        "paragraphs": [
          "Later occultists, artists, musicians, filmmakers and subcultures repeatedly reinterpreted Baphomet. Some use it as an esoteric symbol, some as a provocation, some as a shorthand for forbidden knowledge and some simply because the silhouette is instantly recognisable.",
          "The archive therefore treats Baphomet as an image-history rabbit hole: Templars, Lévi, pentagrams, alchemical opposites and the way a drawing can become more famous than the uncertain tradition that preceded it."
        ]
      }
    ],
    "related": [
      "Pentagram",
      "Ceremonial Magic",
      "Seal of Solomon",
      "Abraxas"
    ],
    "catalogue": [
      {
        "label": "Turn your own symbol study into an art print",
        "url": "/products/custom-art-prints"
      },
      {
        "label": "Make an illustrated symbol as stickers",
        "url": "/collections/stickers"
      }
    ]
  },
  {
    "title": "Ceremonial Magic",
    "slug": "ceremonial-magic",
    "type": "Structured ritual magic & grimoire tradition",
    "depth": "Shallows",
    "synopsis": "A broad family of learned magical traditions built around formal ritual, sacred names, diagrams, timing and specialist texts.",
    "lead": [
      "Ceremonial magic is magic arranged like a ceremony: words, gestures, implements, diagrams, prayers, timing and preparation are given an intentional order. Older reference works often describe it mainly as the art of dealing with spirits, but the broader tradition also includes consecration, planetary work, talismans, visionary practice and ritual systems that do not fit neatly into spirit-summoning.",
      "The striking feature is structure. A ceremonial text can read like liturgy, theatre, geometry and technical manual at the same time. That makes the surviving books visually dense with circles, names, seals, tables and marginal instructions."
    ],
    "sections": [
      {
        "heading": "A ritual is a sequence, not just a symbol",
        "paragraphs": [
          "Ceremonial traditions tend to care about order: what is prepared, what is named, where the operator stands, which object belongs where, and how a working begins and ends. The exact theology changes from text to text, but the sense of a deliberately constructed sacred space is remarkably persistent.",
          "That is why a magic circle on its own tells only part of the story. In the manuscripts it belongs to a larger choreography of words, boundaries, tools and attention."
        ]
      },
      {
        "heading": "Names, circles and books",
        "paragraphs": [
          "Divine names and written characters are especially important in many Western ceremonial sources. A name may be spoken, inscribed or arranged around a diagram; a circle may mark ritual space; a pentacle or seal may compress a longer set of correspondences into one portable object.",
          "The visual result is perfect archive material: ruled construction lines, circles over circles, tiny labels, planetary signs, handwritten corrections and the feeling that every mark has been put there for a reason."
        ]
      },
      {
        "heading": "One label, many traditions",
        "paragraphs": [
          "Modern writers often use 'ceremonial magic' as a convenient umbrella for material separated by centuries, languages and religious settings. It is better treated as a family resemblance than one unchanging system.",
          "Follow it deeper and the term breaks apart into grimoires, Solomonic texts, planetary magic, magical alphabets, invocation, exorcism and later occult orders that reorganised older material into new systems."
        ]
      }
    ],
    "related": [
      "Lemegeton",
      "Invocation",
      "Exorcism",
      "Magic Squares",
      "Seal of Solomon"
    ],
    "catalogue": []
  },
  {
    "title": "Divination",
    "slug": "divination",
    "type": "Umbrella term for reading signs & seeking hidden knowledge",
    "depth": "Surface",
    "synopsis": "The great umbrella over cards, lots, dreams, omens, stars, birds, weather, wood, rings and hundreds of stranger ways of asking what cannot simply be known.",
    "lead": [
      "Divination is less one practice than an enormous family of practices for obtaining guidance or hidden knowledge through signs. The sign might be deliberately produced by drawing cards or casting lots, or it might already be present in the world: weather, birds, dreams, an accidental meeting, a crack in a surface or an object found in the road.",
      "Once you notice the family resemblance, the occult dictionary suddenly connects. Aeromancy, geomancy, xylomancy, dactylomancy, tarot and apantomancy are not isolated curiosities; they are different answers to the same old question: if the world could reply, what would count as its reply?"
    ],
    "sections": [
      {
        "heading": "Made signs and found signs",
        "paragraphs": [
          "Some systems manufacture a controlled random event and then interpret it. Cards are shuffled, lots are drawn, dots are made or objects are cast. Others depend on attention to something that was not arranged for the question at all: an animal crossing the path, a dream, a wind change or a pattern in fire.",
          "That distinction gives the archive two different visual moods: the diviner's table and the open world outside it."
        ]
      },
      {
        "heading": "From intuition to elaborate systems",
        "paragraphs": [
          "Divination can be free-form and intuitive, but many traditions are highly structured. Geomancy has named figures and houses; astrology has charts and calculated positions; tarot acquired extensive systems of correspondences; omen traditions can contain long lists of local interpretations.",
          "The system is often what turns chance into a readable language. Without an interpretive framework, the same mark is only a mark."
        ]
      },
      {
        "heading": "A perfect rabbit-hole hub",
        "paragraphs": [
          "Divination is one of the archive's broadest surface terms because almost every culture has produced ways of asking uncertainty to take a shape. The methods can contradict each other and still remain culturally meaningful.",
          "From here the useful move is not to ask which method is 'the real one' but to choose a material: sky, cards, rings, flour, wood, dreams, numbers or chance encounters, then follow that branch down."
        ]
      }
    ],
    "related": [
      "Tarot",
      "Geomancy",
      "Aeromancy",
      "Xylomancy",
      "Apantomancy",
      "Dactylomancy"
    ],
    "catalogue": []
  },
  {
    "title": "Exorcism",
    "slug": "exorcism",
    "type": "Religious & magical expulsion rite",
    "depth": "Shallows",
    "synopsis": "Rites intended to expel or command an unwanted spirit, found in religious, folkloric and magical traditions under very different rules.",
    "lead": [
      "Exorcism is the attempt to expel, bind or command a harmful or unwanted spiritual presence. The word now strongly suggests dramatic possession narratives, but historical exorcism also appears in blessings of places, objects and people, in formal religious rites and in magical texts that use sacred names as authority.",
      "Older occult encyclopedias often merge exorcism with conjuration because both can involve commanding language. The distinction is useful: an invocation asks a presence in; an exorcism is usually concerned with forcing something out or setting a boundary against it."
    ],
    "sections": [
      {
        "heading": "Authority expressed through words",
        "paragraphs": [
          "Across many historical examples, the important idea is that the exorcist does not rely on personal strength alone. Authority comes from a deity, sacred name, scripture, prayer, relic, sign or inherited ritual form.",
          "That makes exorcism visually a subject of thresholds and language: doorways, crossed marks, written names, bells, candles, water, books and the emphatic act of drawing a boundary."
        ]
      },
      {
        "heading": "House, body, object",
        "paragraphs": [
          "Folklore and ritual sources do not restrict unwanted influence to a single kind of place. A person may be described as possessed; a house as haunted; an object as contaminated or dangerous. The response changes with the tradition, but cleansing and expulsion repeatedly use the vocabulary of opening, closing, naming and removing.",
          "The archive keeps these as historical and folkloric ideas rather than practical medical explanations. Experiences of illness or distress need ordinary real-world care regardless of how older texts interpreted them."
        ]
      },
      {
        "heading": "Where it connects",
        "paragraphs": [
          "Exorcism sits between religion, ceremonial magic and ghost folklore. It leads naturally to adjuration, invocation, magical words, haunted-house traditions and the grimoires that turned commanding formulae into elaborate written systems."
        ]
      }
    ],
    "related": [
      "Adjuration",
      "Invocation",
      "Ceremonial Magic",
      "Abracadabra",
      "Lemegeton"
    ],
    "catalogue": []
  },
  {
    "title": "Familiars",
    "slug": "familiars",
    "type": "Spirit companions in witchcraft & magical folklore",
    "depth": "Surface",
    "synopsis": "Attendant spirits, imps and animal-shaped companions that appear throughout witchcraft accusations, folklore and later magical imagination.",
    "lead": [
      "A familiar is a supernatural helper associated with a witch, magician or cunning person. In early modern European material, familiars may appear as small animals, strange imps, named spirits or beings thought to inhabit rings and other objects. The black cat became the popular shorthand, but the archive is much stranger than cats alone.",
      "Trial records and demonological books are not neutral descriptions of what people actually practised: they are shaped by accusation, fear and interrogation. Familiars therefore belong simultaneously to folklore, legal history and the imaginative machinery of witchcraft."
    ],
    "sections": [
      {
        "heading": "Not always an ordinary animal",
        "paragraphs": [
          "Stories give familiars dog, cat, toad, bird and hybrid forms, but often insist that the creature is more than an animal. Some have names and personalities; some carry messages; some are said to live inside charms, bottles or rings.",
          "That slippage between household creature and invisible helper is precisely why familiar imagery works so well. The ordinary animal can remain cute until one tiny detail makes it wrong."
        ]
      },
      {
        "heading": "The famous black dog",
        "paragraphs": [
          "One story repeatedly attached to Cornelius Agrippa says that he was accompanied by a black dog believed to be his familiar. The tale grew around the reputation of a scholar already surrounded by legends of forbidden books and magical knowledge.",
          "Whether or not such stories tell us anything about Agrippa, they show how easily a companion animal becomes evidence once a person has acquired a magical reputation."
        ]
      },
      {
        "heading": "From accusation to mascot",
        "paragraphs": [
          "Modern witch imagery often turns the familiar from sinister evidence into friend, mascot or magical partner. That reversal is worth keeping visible: a figure once used to make accusations frightening can become a symbol of companionship and personal identity."
        ]
      }
    ],
    "related": [
      "Witchcraft",
      "Amulets",
      "Jinn",
      "Baphomet"
    ],
    "catalogue": [
      {
        "label": "Turn a familiar character into stickers",
        "url": "/collections/stickers"
      }
    ]
  },
  {
    "title": "Fibonacci Sequence",
    "slug": "fibonacci-sequence",
    "type": "Number pattern, proportion & visual construction",
    "depth": "Deep Water",
    "synopsis": "The sequence 1, 1, 2, 3, 5, 8… and the beautiful visual constructions that connect mathematics, growth patterns and modern sacred-geometry imagery.",
    "lead": [
      "The Fibonacci sequence begins simply: each new number is the sum of the two before it. Write 1, 1, 2, 3, 5, 8, 13, 21 and keep going, and the ratio between neighbouring terms approaches the golden ratio. That mathematical relationship is real; the extravagant claim that every beautiful thing in nature is secretly built from it is not.",
      "For an illustrated occult archive, the useful territory lies between those two facts. Fibonacci squares and spiral approximations are genuine mathematical constructions, while shells, flowers, seed heads and sacred-geometry diagrams have accumulated a much larger symbolic story around them."
    ],
    "sections": [
      {
        "heading": "Fibonacci spiral versus golden spiral",
        "paragraphs": [
          "A familiar classroom construction places squares whose side lengths follow Fibonacci numbers, then draws quarter-circle arcs through them. The result approaches the shape of a golden spiral as the numbers grow, but the two are not literally the same curve.",
          "That small distinction makes a much better journal page than repeating the myth that one perfect spiral has been stamped onto all of nature."
        ]
      },
      {
        "heading": "Where nature really becomes interesting",
        "paragraphs": [
          "Fibonacci numbers do appear in some plant-growth patterns, especially arrangements connected with phyllotaxis. Seed heads and leaf arrangements can produce counts related to neighbouring Fibonacci numbers because of efficient packing and growth, although biology is messier than an occult poster would suggest.",
          "The archive can keep both layers visible: the measured botanical study and the human urge to turn a recurring pattern into a cosmic signature."
        ]
      },
      {
        "heading": "A hidden geometry for artwork",
        "paragraphs": [
          "Fibonacci grids are especially useful as under-drawings. A composition can be built over the squares and spiral, then allow the construction to disappear beneath paint, print or a second image. The geometry remains part of how the work was made even when the viewer cannot see it.",
          "That makes Fibonacci a natural bridge into OPS 'Beneath the Ink' ideas: hidden layers, underprints, secret marks, magic squares and symbols embedded below the visible surface."
        ]
      }
    ],
    "related": [
      "Magic Squares",
      "Pentagram",
      "Seal of Solomon",
      "Abracadabra"
    ],
    "catalogue": [
      {
        "label": "Print artwork built on hidden geometry",
        "url": "/products/custom-art-prints"
      }
    ]
  },
  {
    "title": "Geomancy",
    "slug": "geomancy",
    "type": "Divination through figures made from marks or points",
    "depth": "Shallows",
    "synopsis": "A structured divinatory system in which apparently random marks resolve into a small alphabet of figures that can be read like a chart.",
    "lead": [
      "Western geomancy turns chance marks into sixteen named figures. Traditionally the marks might be made as rows of points in sand, earth or on paper; their odd or even counts are reduced to single or paired points. Those simple binary-looking patterns are then combined and interpreted through a formal chart.",
      "The name literally suggests divination by earth, but the mature system is much more abstract than simply staring at soil. It is almost a handmade computer: random input, reduction into a tiny symbolic alphabet, then a sequence of rules that produces a reading."
    ],
    "sections": [
      {
        "heading": "Sixteen little figures",
        "paragraphs": [
          "Each geomantic figure consists of four levels, with either one point or two points on each level. That gives sixteen possible patterns. Traditional names such as Via, Populus, Fortuna Major and Carcer turn those compact dot arrangements into characters with different tendencies.",
          "The figures are visually excellent: minimal enough to become icons, but meaningful only after the reader learns the hidden alphabet."
        ]
      },
      {
        "heading": "From loose marks to a house chart",
        "paragraphs": [
          "A reading does not stop at the first figures produced. Figures are combined to generate others and placed into structures sometimes compared with astrological houses. The result links spontaneous mark-making with a surprisingly strict interpretive machine.",
          "That mixture of randomness and rule is a recurring occult theme shared with magic squares, lots and card systems."
        ]
      },
      {
        "heading": "Earth as interface",
        "paragraphs": [
          "Older descriptions sometimes reduce geomancy to lines or circles drawn on the ground. The richer image is the diviner making hurried points in earth or sand, then discovering that the messy marks collapse into an orderly symbolic chart."
        ]
      }
    ],
    "related": [
      "Divination",
      "Magic Squares",
      "Dactylomancy",
      "Apantomancy",
      "Xylomancy"
    ],
    "catalogue": []
  },
  {
    "title": "Hermes Trismegistus",
    "slug": "hermes-trismegistus",
    "type": "Legendary sage of the Hermetic tradition",
    "depth": "Shallows",
    "synopsis": "The 'thrice-great Hermes', a syncretic sage linking Greek Hermes and Egyptian Thoth whose name became attached to the Hermetic writings.",
    "lead": [
      "Hermes Trismegistus is not best understood as one historical author. The name belongs to a legendary sage created in the meeting of Greek and Egyptian religious worlds, combining qualities associated with Hermes and Thoth. Texts attributed to Hermes became foundational to what later Europeans called the Hermetic tradition.",
      "Because the name arrived attached to teachings on mind, cosmos, divinity, astrology and alchemy, later occult writers imagined Hermes as an almost impossibly ancient master from whom whole sciences descended."
    ],
    "sections": [
      {
        "heading": "A name that became an author",
        "paragraphs": [
          "Ancient and late-antique writings circulated under the authority of Hermes, including philosophical dialogues now grouped as the Corpus Hermeticum and technical material concerned with astrology, alchemy and magic. They were written by different authors over time rather than dictated by one timeless sage.",
          "The imagined author nevertheless became culturally real: a portrait, a teacher and a convenient ancestor for later traditions."
        ]
      },
      {
        "heading": "Thoth, Hermes and the library of everything",
        "paragraphs": [
          "Egyptian Thoth was associated with writing, learning and divine calculation; Greek Hermes with messages, boundaries and clever movement. Their identification helped produce a figure who could plausibly be credited with sacred books covering almost every hidden science.",
          "That is why Hermetic imagery naturally fills with tablets, ibis and caduceus echoes, stars, books and diagrams of correspondence."
        ]
      },
      {
        "heading": "The Hermetic rabbit hole",
        "paragraphs": [
          "From Hermes the archive branches into alchemy, astrology, Renaissance magic, the Emerald Tablet, macrocosm and microcosm, and the enduring idea that patterns repeat between different levels of reality."
        ]
      }
    ],
    "related": [
      "Ceremonial Magic",
      "Magic Squares",
      "Pentagram",
      "Zodiac"
    ],
    "catalogue": []
  },
  {
    "title": "Invocation",
    "slug": "invocation",
    "type": "Ritual calling, address & invitation",
    "depth": "Deep Water",
    "synopsis": "Ritual language used to call upon a deity, spirit, power or quality - a useful counterpart to commands, banishings and evocations.",
    "lead": [
      "Invocation is the act of calling upon a spiritual power through words, prayer, names or ritual address. Occult books do not always use the term consistently, but a useful modern distinction treats invocation as calling a power toward or into the ritual relationship, while evocation often suggests calling a presence forth or before the operator.",
      "The older encyclopedia sends Invocation straight to Necromancy, showing how tightly older reference writers bundled different forms of spirit communication together. A modern archive can keep the terms separate while still showing why their borders blur."
    ],
    "sections": [
      {
        "heading": "The voice changes the room",
        "paragraphs": [
          "An invocation is often written differently from explanatory prose. It addresses rather than describes: names are repeated, titles accumulate, verbs become direct and the speaker establishes a relationship with whatever is being called.",
          "On a manuscript page that change is visible. Ordinary notes give way to enlarged names, rhythmic lines, capitals, circles or marginal marks that signal 'the ritual voice starts here'."
        ]
      },
      {
        "heading": "Prayer, magic or theatre?",
        "paragraphs": [
          "Depending on the tradition, an invocation may resemble prayer, hymn, magical formula or dramatic speech. Those categories overlap historically, which is one reason ceremonial texts can feel both devotional and theatrical.",
          "The archive is interested in the form of the language as much as any supernatural claim: how people design words to sound authoritative, reverent or transformative."
        ]
      },
      {
        "heading": "A useful contrast",
        "paragraphs": [
          "Invocation pairs naturally with adjuration, exorcism and ceremonial magic. One asks or calls; another commands; another expels. Real texts mix those modes freely, but the comparison makes the ritual vocabulary much easier to navigate."
        ]
      }
    ],
    "related": [
      "Adjuration",
      "Exorcism",
      "Ceremonial Magic",
      "Necromancy",
      "Lemegeton"
    ],
    "catalogue": []
  },
  {
    "title": "Jinn",
    "slug": "jinn",
    "type": "Supernatural beings in Arabic & Islamic tradition",
    "depth": "Shallows",
    "synopsis": "A broad class of beings from Arabic and Islamic traditions, neither simply ghosts nor simply demons, with their own moral agency and enormous folklore.",
    "lead": [
      "Jinn are supernatural beings deeply rooted in Arabic and Islamic traditions. The Qur'an describes jinn as created from a smokeless flame of fire and treats them as morally responsible beings capable of belief or disbelief. That already makes the common English shortcut 'jinn equals demon' misleading.",
      "Folklore then multiplies the possibilities: jinn inhabit ruins, deserts, wells and thresholds; become visible or invisible; take animal or human forms; marry, quarrel, travel and interfere with people. Different regions tell very different stories."
    ],
    "sections": [
      {
        "heading": "A category, not one monster",
        "paragraphs": [
          "The word covers a class of beings rather than a single species with one appearance. Later storytelling introduces named types and powerful individuals, while European translations often collapsed them into 'genies' or 'demons'.",
          "Keeping the category broad makes the imagery much more interesting: wind, fire without smoke, abandoned architecture, footprints, animal forms and something present without needing one canonical face."
        ]
      },
      {
        "heading": "The bottle is only one branch",
        "paragraphs": [
          "The bottled genie became globally familiar through literary and popular adaptations, but confinement in vessels is only one motif among many. Talismans, seals and stories of King Solomon controlling spirits helped Western occult writers connect jinn to Solomonic magic.",
          "That connection is a historical meeting point between traditions, not proof that all jinn folklore belongs to one grimoire system."
        ]
      },
      {
        "heading": "Respect the tradition while following the folklore",
        "paragraphs": [
          "Jinn remain part of living religious belief as well as folklore and popular culture. The archive therefore distinguishes Islamic references, regional stories and later Western occult reinterpretation rather than blending them into a generic demon catalogue."
        ]
      }
    ],
    "related": [
      "Seal of Solomon",
      "Lemegeton",
      "Familiars",
      "Ceremonial Magic"
    ],
    "catalogue": []
  },
  {
    "title": "Kabala",
    "slug": "kabala",
    "type": "Historical occult spelling around Kabbalistic traditions",
    "depth": "Shallows",
    "synopsis": "A doorway into Jewish Kabbalah and the later Christian Cabala and Hermetic Qabalah that borrowed, translated and transformed its ideas.",
    "lead": [
      "'Kabala' is the spelling used by many older occult reference books, but the word opens onto several related traditions that should not be flattened into one. Jewish Kabbalah is a body of Jewish mystical thought and textual interpretation; Christian Cabala adapted Kabbalistic ideas in a Christian intellectual setting; Hermetic Qabalah later reorganised selected concepts inside Western occult systems.",
      "That distinction matters because occult books often present borrowed Hebrew terms as if they had always belonged to one universal magical code. The history is more interesting when the layers remain visible."
    ],
    "sections": [
      {
        "heading": "The Tree and the ten sefirot",
        "paragraphs": [
          "One of the most recognisable Kabbalistic diagrams is the Tree of Life, commonly used to arrange ten sefirot - modes or emanations through which divine reality is described. Later occult systems added their own correspondences to planets, tarot cards, colours and paths.",
          "The familiar occult poster is therefore a historical stack: Jewish mystical vocabulary underneath, then later layers of Christian and Hermetic interpretation around it."
        ]
      },
      {
        "heading": "Letters as more than letters",
        "paragraphs": [
          "Hebrew letters and numerical interpretation became especially attractive to Western occultists interested in sacred names, gematria and magical alphabets. Older encyclopedias repeatedly route magical words and diagrams back toward 'Kabala' whether or not the connection is historically direct.",
          "That makes this entry a useful checkpoint: when a magical diagram claims to be Kabbalistic, ask which Kabbalah, which period and which layer of interpretation."
        ]
      },
      {
        "heading": "Three spellings, three signals",
        "paragraphs": [
          "Modern writers often use Kabbalah for Jewish traditions, Cabala for Christian adaptations and Qabalah for Hermetic occult systems. Usage is not perfectly consistent, but the spelling can warn the reader that the surrounding framework has changed."
        ]
      }
    ],
    "related": [
      "Magic Squares",
      "Seal of Solomon",
      "Lemegeton",
      "Hermes Trismegistus"
    ],
    "catalogue": []
  },
  {
    "title": "Lemegeton",
    "slug": "lemegeton",
    "type": "Early modern grimoire compilation, also called The Lesser Key of Solomon",
    "depth": "Shallows",
    "synopsis": "A manuscript compilation better known as The Lesser Key of Solomon, containing five distinct books - including the Ars Goetia and its famous spirit seals.",
    "lead": [
      "The Lemegeton, or Lesser Key of Solomon, is an early modern grimoire compilation transmitted in seventeenth-century manuscripts. It borrows the authority of the legendary King Solomon, but it is not an ancient book written by Solomon himself.",
      "Its fame today comes disproportionately from the Ars Goetia, the first of its five parts, which catalogues seventy-two spirits with names, offices and seals. The other books make the Lemegeton a much stranger library than the phrase '72 demons' suggests."
    ],
    "sections": [
      {
        "heading": "Five books under one title",
        "paragraphs": [
          "The compilation is commonly divided into Ars Goetia, Ars Theurgia-Goetia, Ars Paulina, Ars Almadel and Ars Notoria. They do not all do the same thing: the material ranges across spirit catalogues, directional and aerial spirits, planetary or temporal structures, an altar-like device and a final section with a complicated textual history.",
          "Treating the whole Lemegeton as one demon list hides most of its architecture."
        ]
      },
      {
        "heading": "The seventy-two seals",
        "paragraphs": [
          "The Ars Goetia assigns seals to seventy-two named spirits. Those line drawings are often reproduced as if they were one ancient geometrical alphabet, but their manuscript history belongs to a specific grimoire tradition.",
          "They should also be kept distinct from another occult technique: constructing sigils by tracing names through planetary magic squares or kameas. Both produce abstract line symbols, but they are not the same method."
        ]
      },
      {
        "heading": "Solomon as borrowed authority",
        "paragraphs": [
          "Attaching a magical text to Solomon gave it an ancient, biblical and legendary authority. The same gravity gathers around the Seal of Solomon, talisman traditions and stories of spirits confined or commanded by the king.",
          "The result is a network of Solomonic material rather than one neat historical source."
        ]
      }
    ],
    "related": [
      "Seal of Solomon",
      "Ceremonial Magic",
      "Invocation",
      "Magic Squares",
      "Jinn"
    ],
    "catalogue": []
  },
  {
    "title": "Magic Squares",
    "slug": "magic-squares",
    "type": "Number grids, planetary kameas & sigil scaffolds",
    "depth": "Deep Water",
    "synopsis": "Number grids whose rows and columns share a total, transformed in some occult traditions into planetary diagrams and frameworks for constructed sigils.",
    "lead": [
      "A magic square is a grid of numbers arranged so that rows, columns and often the main diagonals add to the same total. The mathematics exists independently of occultism, but Western esoteric traditions attached particular square sizes to the classical planets and called them planetary tables or kameas.",
      "Once a grid is treated as a map, another trick becomes possible: letters or numbers in a name can be converted to positions and joined by lines. That is one route by which a mathematical table can become the hidden skeleton of a sigil."
    ],
    "sections": [
      {
        "heading": "The square before the spell",
        "paragraphs": [
          "The simplest important point is mathematical: magic squares are combinatorial objects, not inherently magical. Their striking regularity made them attractive in many cultures long before and outside modern occult use.",
          "Esoteric interpretation adds a second layer by assigning grids to planets, metals, divine names, spirits or talismans."
        ]
      },
      {
        "heading": "Kameas and traced names",
        "paragraphs": [
          "In later Western occult practice, a planetary square can function as a coordinate system. A name is converted into numbers associated with the grid, then a path is traced from cell to cell. The resulting line can be stylised into a sigil.",
          "This is the technique people often accidentally project onto every old seal. It is real, but it does not explain all Solomonic or Goetic seals."
        ]
      },
      {
        "heading": "Perfect hidden-layer material",
        "paragraphs": [
          "A kamea is naturally suited to underprinting and buried geometry. The finished artwork can completely cover the number grid while still being composed over it, leaving a secret mathematical structure beneath the visible image.",
          "That connects the historical archive directly to Fibonacci grids, pentagrams, sigils and OPS 'Beneath the Ink' experiments without pretending the print itself acquires supernatural properties."
        ]
      }
    ],
    "related": [
      "Fibonacci Sequence",
      "Pentagram",
      "Seal of Solomon",
      "Kabala",
      "Lemegeton"
    ],
    "catalogue": [
      {
        "label": "Print an artwork built over a hidden grid",
        "url": "/products/custom-art-prints"
      }
    ]
  },
  {
    "title": "Necromancy",
    "slug": "necromancy",
    "type": "Divination and magic concerned with the dead",
    "depth": "Shallows",
    "synopsis": "Originally a word for divination through the dead, later expanded in folklore and fiction into a much larger idea of summoning or raising them.",
    "lead": [
      "Necromancy literally points toward divination involving the dead. Ancient stories often focus on consulting a dead person for knowledge rather than creating an undead servant or physically resurrecting a corpse. The spectacular modern fantasy meaning grew from a much wider and messier history.",
      "The older occult encyclopedia explicitly begins with the Greek roots for 'dead' and 'divination' and notes traditions of descending to the realm of the dead to seek answers. That is a useful correction to the pop-culture picture."
    ],
    "sections": [
      {
        "heading": "Consultation before resurrection",
        "paragraphs": [
          "Classical and biblical narratives include attempts to obtain information from the dead, while later magical literature develops elaborate ideas about apparitions, spirits and constrained communication. The core question is knowledge: what can the dead be asked that the living do not know?",
          "Once that is understood, necromancy reconnects naturally to the larger family of divination instead of sitting alone as 'dark magic'."
        ]
      },
      {
        "heading": "Why graveyards dominate the imagery",
        "paragraphs": [
          "Later European folklore and magical writing increasingly surround necromancy with night, ruins, graveyards, bones, black cloth and solemn locations. Those details became an extraordinarily durable visual language, then fiction amplified them further.",
          "A good archive image can show that theatrical growth without presenting a dangerous ritual as a how-to guide."
        ]
      },
      {
        "heading": "The word keeps escaping its definition",
        "paragraphs": [
          "Modern fantasy frequently uses necromancy for animation of skeletons, creation of undead armies or any magic involving death. That is a later imaginative branch, but it is now culturally important enough to sit beside the older divinatory meaning rather than being dismissed."
        ]
      }
    ],
    "related": [
      "Divination",
      "Invocation",
      "Exorcism",
      "Ceremonial Magic",
      "Vampire"
    ],
    "catalogue": []
  },
  {
    "title": "Obeah",
    "slug": "obeah",
    "type": "Caribbean spiritual and magical traditions under a contested colonial label",
    "depth": "Shallows",
    "synopsis": "A varied Caribbean term shaped by African-diasporic practice, healing, protection, fear, colonial law and centuries of outsiders describing it badly.",
    "lead": [
      "Obeah is not one neatly bounded religion or a single book of spells. Across parts of the Caribbean, the word has been used for a changing range of spiritual, protective, healing and magical practices with strong African-diasporic histories. Colonial authorities also used the label broadly when criminalising practices they feared or wanted to control.",
      "That history makes old occult encyclopedias especially tricky sources here. Their language often reflects colonial assumptions, so the OPS archive uses them to locate terminology while rebuilding the explanation with much more care."
    ],
    "sections": [
      {
        "heading": "One word, many islands",
        "paragraphs": [
          "Meanings differ by place and period. Practices associated with Obeah may involve healing knowledge, protective work, divination, spirit concerns, charms or feared harmful magic, but no single checklist applies across the Caribbean.",
          "The archive therefore treats geographical and historical context as part of the term rather than an optional footnote."
        ]
      },
      {
        "heading": "Criminalisation changed the record",
        "paragraphs": [
          "Anti-Obeah laws and prosecutions helped produce written records, but those records are not transparent windows into practitioners' lives. They were created by courts, police, missionaries and colonial writers with their own agendas.",
          "The result is a subject where the archive itself has to be read suspiciously: who is naming the practice, and why? "
        ]
      },
      {
        "heading": "Visual material without caricature",
        "paragraphs": [
          "A responsible illustrated treatment should focus on objects, plants, protective packets, written fragments, domestic spaces and archival documents where evidence supports them, rather than recycling generic 'voodoo' imagery from unrelated traditions."
        ]
      }
    ],
    "related": [
      "Amulets",
      "Divination",
      "Witchcraft",
      "Apantomancy"
    ],
    "catalogue": []
  },
  {
    "title": "Pentagram",
    "slug": "pentagram",
    "type": "Five-pointed star, geometry & occult emblem",
    "depth": "Surface",
    "synopsis": "A five-pointed star with a history far older and broader than its modern reputation as an automatic sign of satanism.",
    "lead": [
      "The pentagram is one of the simplest shapes in the archive and one of the most overloaded. Five straight strokes produce a star whose internal geometry contains repeated golden-ratio relationships. Long before modern occultism, five-pointed stars appeared in mathematical, religious, protective and decorative settings.",
      "Nineteenth-century occult writers gave orientation a stronger moral symbolism, helping popular culture turn the upright and inverted forms into opposing signs. That later convention is influential, but it is not a universal law built into the geometry."
    ],
    "sections": [
      {
        "heading": "A star full of ratios",
        "paragraphs": [
          "In a regular pentagram, line segments divide one another in the golden ratio again and again. That gives the shape a natural route into Fibonacci, proportion and the history of mathematical mysticism.",
          "The geometry is beautiful even before any supernatural meaning is added, which may be one reason symbolic systems kept finding uses for it."
        ]
      },
      {
        "heading": "Microcosm, protection and later occultism",
        "paragraphs": [
          "Western esoteric authors used the pentagram for the microcosm, the elements, the human figure and ritual protection. Magical diagrams place it beside triangles, hexagrams, circles and divine names.",
          "Different groups assign different correspondences, so a pentagram should always be read in context rather than translated automatically."
        ]
      },
      {
        "heading": "The upside-down problem",
        "paragraphs": [
          "The inverted pentagram acquired strongly negative or satanic associations in some nineteenth- and twentieth-century occult systems and later popular culture. Earlier uses are more varied. Orientation matters in some traditions, but history refuses the simple internet rule that one direction is universally good and the other universally evil."
        ]
      }
    ],
    "related": [
      "Fibonacci Sequence",
      "Magic Squares",
      "Baphomet",
      "Seal of Solomon"
    ],
    "catalogue": [
      {
        "label": "Print your own geometric artwork",
        "url": "/products/custom-art-prints"
      },
      {
        "label": "Turn a symbol into stickers",
        "url": "/collections/stickers"
      }
    ]
  },
  {
    "title": "Quirinus",
    "slug": "quirinus",
    "type": "Legendary truth-telling stone from a hoopoe's nest",
    "depth": "Abyss",
    "synopsis": "A tiny, wonderfully obscure lapidary claim: a stone from a hoopoe's nest said to make a sleeping person reveal their wrongdoing.",
    "lead": [
      "Quirinus - also printed as Quirus in old occult reference material - is exactly the sort of thing that belongs at the bottom of the iceberg. The entry describes a 'juggling stone' found in the nest of a hoopoe and claims that placing it on the breast of a sleeping person forces them to disclose their misdeeds.",
      "There is almost no need to inflate it. The strange specificity is the story: one bird, one impossible stone, one sleeping confession. It feels like a discarded quest item from a game because old lapidaries and magical encyclopedias were already full of objects that behaved like that."
    ],
    "sections": [
      {
        "heading": "The occult cabinet at its weirdest",
        "paragraphs": [
          "Magical stone lore frequently assigns a mineral one sharply defined power: protection from lightning, courage, invisibility, safe childbirth, truth-telling or prophetic dreams. Quirinus belongs to that cabinet tradition even when the actual identity of the stone is impossible to recover.",
          "The obscurity is useful because it shows how much occult reference material lies below famous symbols and named demons."
        ]
      },
      {
        "heading": "Why a hoopoe?",
        "paragraphs": [
          "Hoopoes already carry an enormous folklore across Europe, the Middle East and North Africa. They appear in stories of hidden knowledge and in the famous Persian poem *The Conference of the Birds*. Whether any of that explains this particular stone claim is uncertain, but the bird makes the object much more memorable."
        ]
      },
      {
        "heading": "How to illustrate a nearly-lost term",
        "paragraphs": [
          "The page wants a natural-history notebook rather than a fantasy gem: hoopoe feather studies, a nest, one ambiguous pebble circled in red pencil and a tiny marginal note about the sleeper who cannot keep a secret."
        ]
      }
    ],
    "related": [
      "Aetites",
      "Amulets",
      "Apantomancy",
      "Xylomancy"
    ],
    "catalogue": []
  },
  {
    "title": "Rosicrucians",
    "slug": "rosicrucians",
    "type": "Manifestos, mythical brotherhood & esoteric movement",
    "depth": "Shallows",
    "synopsis": "A legendary brotherhood announced by mysterious seventeenth-century manifestos whose rose-and-cross imagery outlived the question of who, if anyone, belonged to it.",
    "lead": [
      "The Rosicrucian mystery begins with texts before it begins with a clearly documented organisation. Early seventeenth-century manifestos announced a secret learned fraternity associated with the legendary Christian Rosenkreuz and promised a reformation of knowledge, religion and society.",
      "Readers immediately wanted to know whether the brotherhood was real, allegorical, satirical or hidden too well to find. That uncertainty became part of Rosicrucianism's power: a secret society that could recruit the imagination before it recruited any provable members."
    ],
    "sections": [
      {
        "heading": "The manifestos create the mystery",
        "paragraphs": [
          "The *Fama Fraternitatis* and *Confessio Fraternitatis*, followed by the strange alchemical romance known as the *Chymical Wedding of Christian Rosenkreutz*, created a public puzzle around an allegedly secret fraternity.",
          "The rose and cross became the enduring visual shorthand, later adopted and reinterpreted by actual esoteric organisations."
        ]
      },
      {
        "heading": "Brotherhood as literary technology",
        "paragraphs": [
          "A hidden order is an extraordinarily effective storytelling device. It implies missing archives, unseen teachers and knowledge circulating just beyond the reader's reach. Early Rosicrucian texts exploited exactly that tension.",
          "The archive can therefore treat secrecy itself as part of the design: sealed letters, anonymous pamphlets, rose-cross diagrams and correspondence with no visible recipient."
        ]
      },
      {
        "heading": "Later orders are real - and different",
        "paragraphs": [
          "Nineteenth- and twentieth-century organisations adopted Rosicrucian names and symbolism, but they should not simply be projected backwards as proof of one continuous ancient brotherhood. The movement has several historical lives."
        ]
      }
    ],
    "related": [
      "Hermes Trismegistus",
      "Ceremonial Magic",
      "Kabala",
      "Lemegeton"
    ],
    "catalogue": []
  },
  {
    "title": "Seal of Solomon",
    "slug": "seal-of-solomon",
    "type": "Solomonic emblem, protective seal & shifting geometric symbol",
    "depth": "Shallows",
    "synopsis": "A legendary seal of King Solomon represented in different periods by interlaced triangles, pentagrams and other magical designs.",
    "lead": [
      "The Seal of Solomon is a moving target. Stories give the biblical king a ring or seal granting authority over spirits, while later magical and decorative traditions represent the seal through different geometries - especially the hexagram and, in some sources, the pentagram.",
      "That is why it should not be reduced to one SVG labelled 'the original'. The power of the motif lies in the idea of a royal sign that identifies, protects, binds and commands."
    ],
    "sections": [
      {
        "heading": "Legend before standard logo",
        "paragraphs": [
          "Solomon's reputation for wisdom grew into legends of magical authority. Rings, seals and engraved signs became material evidence inside those stories, even though surviving traditions disagree about their exact form.",
          "The familiar interlaced-triangle design eventually became strongly associated with the name, but other manuscripts and magical books use different forms."
        ]
      },
      {
        "heading": "Not the same as the 72 Goetic seals",
        "paragraphs": [
          "The phrase 'Seal of Solomon' refers to the broader Solomonic emblem tradition. The seventy-two individual seals printed for spirits in the Ars Goetia are a different set of designs inside the Lemegeton tradition.",
          "Keeping those two things separate makes the archive much clearer and prevents every Solomonic line drawing being thrown into one bag."
        ]
      },
      {
        "heading": "Seal, boundary, signature",
        "paragraphs": [
          "A seal can mean authentication, ownership, closure and authority before any occult interpretation begins. Solomonic magic exploits all of those ordinary meanings at once: the mark identifies the king and becomes a sign that other beings are expected to recognise."
        ]
      }
    ],
    "related": [
      "Lemegeton",
      "Pentagram",
      "Magic Squares",
      "Jinn",
      "Amulets"
    ],
    "catalogue": [
      {
        "label": "Turn your own seal design into stickers",
        "url": "/collections/stickers"
      }
    ]
  },
  {
    "title": "Tarot",
    "slug": "tarot",
    "type": "Card game, divination system & visual symbolic language",
    "depth": "Surface",
    "synopsis": "Seventy-eight cards whose history begins with fifteenth-century European card games and later expands into divination, occult correspondence and an enormous art tradition.",
    "lead": [
      "Tarot cards were not discovered in an Egyptian temple and they were not originally a secret book of ancient magic. The earliest surviving tarot packs belong to fifteenth-century Italy and were made for card games. Their later occult life is real, but it arrives centuries after the cards themselves.",
      "That later life transformed a deck into a symbolic machine. Twenty-two trump cards became the Major Arcana; the four suits became the Minor Arcana; occultists connected cards to astrology, Hebrew letters, Kabbalah and paths of initiation. Artists then rebuilt the imagery again and again."
    ],
    "sections": [
      {
        "heading": "The cards before the occult story",
        "paragraphs": [
          "Early tarot - or *tarocchi* - belongs to the history of European playing cards. Painted luxury decks survive alongside later printed packs, and their trump imagery includes figures such as the Fool, Emperor, Wheel, Death, Sun and World.",
          "Knowing that history does not make tarot less mysterious. It makes the transformation into an occult system easier to see."
        ]
      },
      {
        "heading": "Eighteenth-century reinvention",
        "paragraphs": [
          "From the eighteenth century onward, writers increasingly claimed ancient Egyptian, Kabbalistic or initiatory origins for tarot. Those origin stories are historically shaky, but they shaped the decks and interpretive systems used today.",
          "A useful archive page therefore separates card history from occult reception without pretending one cancels the other."
        ]
      },
      {
        "heading": "A machine for making images",
        "paragraphs": [
          "Tarot is especially important to OPS because the system expects illustration. Every deck is an argument about what the same recurring titles should look like. A new artist can preserve the structure while completely changing the world around it.",
          "That makes tarot not only a divination topic but an endless source of composition, character, border, icon and symbolic-reference material."
        ]
      }
    ],
    "related": [
      "Divination",
      "Kabala",
      "Zodiac",
      "Pentagram",
      "Apantomancy"
    ],
    "catalogue": [
      {
        "label": "Print your own tarot-inspired artwork",
        "url": "/products/custom-art-prints"
      }
    ]
  },
  {
    "title": "Urim and Thummim",
    "slug": "urim-and-thummim",
    "type": "Biblical divinatory objects of uncertain form",
    "depth": "Deep Water",
    "synopsis": "Mysterious objects associated with the ancient Israelite high priest and decision-making before God, whose exact physical form remains uncertain.",
    "lead": [
      "Urim and Thummim are among the archive's best examples of an object we know mainly through references to its use. Biblical texts associate them with the high priest and with obtaining decisions, but do not give modern readers a clear technical description of what the objects looked like or exactly how they worked.",
      "Later writers often imagine stones, lots or marked objects that yield categorical answers. The uncertainty should remain visible rather than being replaced with a confident reconstruction."
    ],
    "sections": [
      {
        "heading": "An oracle without an instruction manual",
        "paragraphs": [
          "The texts place Urim and Thummim in a priestly context and connect them with questions requiring divine judgement. That has encouraged comparisons with lot-casting, although the surviving evidence does not settle every detail.",
          "Visually the best treatment is therefore an archaeological question mark: breastpiece, pouch, paired objects, fragments of script and several competing reconstruction sketches."
        ]
      },
      {
        "heading": "Yes, no - or something more?",
        "paragraphs": [
          "Some interpretations imagine a binary response system, partly because biblical narratives sometimes frame consultation as a choice between alternatives. Other reconstructions are more elaborate. The archive records the possibilities without pretending one speculative mechanism is established fact."
        ]
      },
      {
        "heading": "A bridge between religion and divination",
        "paragraphs": [
          "Urim and Thummim complicate the modern habit of separating 'religion' from 'divination'. In the ancient context, seeking a divine decision through authorised priestly means was not automatically treated as the forbidden magic later readers might expect."
        ]
      }
    ],
    "related": [
      "Divination",
      "Amulets",
      "Kabala",
      "Apantomancy"
    ],
    "catalogue": []
  },
  {
    "title": "Vampire",
    "slug": "vampire",
    "type": "Restless dead, revenant folklore & modern monster",
    "depth": "Surface",
    "synopsis": "A folklore family of dangerous returning dead that became one of the world's most recognisable monsters after eighteenth-century reports and Gothic fiction.",
    "lead": [
      "The vampire existed before Dracula, and it was not always an elegant immortal with fangs. European revenant traditions describe troublesome dead bodies and spirits in many different ways: swollen corpses, restless neighbours, disease-bringing revenants, shapeshifters and beings blamed for a community's unexplained deaths.",
      "Eighteenth-century reports of vampire panics in parts of eastern and central Europe helped carry the word across western Europe. Literature then refined the creature into the aristocratic, seductive and highly visual monster familiar today."
    ],
    "sections": [
      {
        "heading": "The body that looks too alive",
        "paragraphs": [
          "Historical vampire investigations often interpreted ordinary processes of decomposition as signs that a corpse remained active: bloating, dark fluids, changing skin, apparent growth of hair or nails and blood around the mouth.",
          "Those observations are medically explainable, but within the folklore they became a checklist for identifying the dangerous dead."
        ]
      },
      {
        "heading": "One village problem becomes contagious",
        "paragraphs": [
          "Vampire scares often work socially as much as supernaturally. One unexplained death is followed by another; suspicion settles on a recent burial; opening the grave appears to confirm the fear; and the community now has a story linking separate events.",
          "That pattern is as important to the archive as the monster design itself."
        ]
      },
      {
        "heading": "From revenant to icon",
        "paragraphs": [
          "Gothic literature and theatre gradually supplied the cape, castle, seduction, aristocracy and finally the visual vocabulary cinema made universal. The modern vampire is therefore a layered print: old revenant folklore underneath, literary invention above it."
        ]
      }
    ],
    "related": [
      "Necromancy",
      "Witchcraft",
      "Familiars",
      "Apantomancy"
    ],
    "catalogue": []
  },
  {
    "title": "Witchcraft",
    "slug": "witchcraft",
    "type": "Practice, accusation, folklore & changing identity",
    "depth": "Surface",
    "synopsis": "A word that has described feared harmful magic, prosecuted imaginary conspiracies, folk practices, modern religions and self-chosen identities - never all at once.",
    "lead": [
      "'Witchcraft' is one of those words that becomes less simple the longer you look at it. In early modern European trials it was often an accusation built around harmful magic, pacts, familiars and diabolic conspiracy. Folk healers and cunning people could be accused of witchcraft while describing themselves very differently. Modern witches may use the word positively for religious, magical or personal identities that have little to do with trial-era demonology.",
      "An old encyclopedia can therefore be a dangerous guide if its categories are copied without context. The archive keeps the historical beliefs, but it also marks the difference between what accusers imagined, what practitioners may actually have done and what the word means now."
    ],
    "sections": [
      {
        "heading": "Accusation creates its own mythology",
        "paragraphs": [
          "Witch-trial records contain sabbaths, animal familiars, flying, harmful charms and pacts, but much of this material was produced under interrogation and within legal expectations. Repetition in the records does not automatically prove a hidden organised religion behind them.",
          "The paperwork is evidence of belief and persecution even when it is poor evidence for the supernatural events being alleged."
        ]
      },
      {
        "heading": "Folk magic does not fit the courtroom box",
        "paragraphs": [
          "Protective charms, healing, fortune-telling, herbal knowledge and counter-magic existed alongside fears of maleficium. Local specialists could be valued for one service and suspected of another.",
          "That ambiguous village world is richer than the single image of a satanic witch invented by demonologists."
        ]
      },
      {
        "heading": "The modern word has changed again",
        "paragraphs": [
          "Twentieth- and twenty-first-century witchcraft includes Wicca, traditional witchcraft movements, folk revivals, eclectic practice and people using the aesthetic or identity without one shared theology. The archive treats those as later branches rather than pretending the 1600s and today are the same phenomenon."
        ]
      }
    ],
    "related": [
      "Familiars",
      "Exorcism",
      "Amulets",
      "Vampire",
      "Obeah"
    ],
    "catalogue": []
  },
  {
    "title": "Xylomancy",
    "slug": "xylomancy",
    "type": "Wood, twig & fire omen divination",
    "depth": "Deep Water",
    "synopsis": "An obscure divinatory label for reading wood: fallen sticks, pieces found on a path, stacked logs or the behaviour of fuel in a fire.",
    "lead": [
      "Xylomancy is divination by wood. The old encyclopedia describes reading small pieces of dry wood found in one's path and drawing omens from the arrangement or burning of logs in a fireplace. It even connects the practice with the household saying that a disturbed brand means a visitor is coming.",
      "The term is obscure, but the behaviour is instantly recognisable. People notice how sticks fall, how logs spit, which way a flame leans and whether a piece of wood rolls unexpectedly. Xylomancy gives that everyday pattern-seeking a name."
    ],
    "sections": [
      {
        "heading": "The omen is already on the path",
        "paragraphs": [
          "Like apantomancy, xylomancy can begin with something encountered rather than deliberately cast. A branch points one way, crossed sticks appear underfoot, or an unusual piece of wood interrupts an ordinary walk.",
          "The diviner's tool is attention rather than an elaborate instrument."
        ]
      },
      {
        "heading": "Fire adds a second alphabet",
        "paragraphs": [
          "Once wood enters the hearth, position becomes behaviour. Cracks, sparks, collapses and unexpected movement can all be interpreted as signs in folk belief.",
          "That gives the subject an appealing two-part illustration: quiet twigs on a pale page above, red-brown fire studies below."
        ]
      },
      {
        "heading": "One of the deep-water '-mancies'",
        "paragraphs": [
          "Xylomancy belongs to the enormous historical habit of naming divination after its material. Following the suffix '-mancy' through the archive turns almost anything - sky, flour, rings, wood - into a possible language of signs."
        ]
      }
    ],
    "related": [
      "Divination",
      "Apantomancy",
      "Aeromancy",
      "Geomancy",
      "Quirinus"
    ],
    "catalogue": []
  },
  {
    "title": "Y-Kim (Book of)",
    "slug": "y-kim-book-of",
    "type": "Antiquarian occult name for the Yi Jing / I Ching",
    "depth": "Deep Water",
    "synopsis": "An old Western occult rendering of the Chinese Yi Jing, valuable partly because it shows how nineteenth-century esoteric writers tried to force distant traditions into one universal system.",
    "lead": [
      "The old encyclopedia calls this the 'Y-Kim' and repeats Éliphas Lévi's attempt to connect it with Kabbalah, Solomon and a primordial universal science. The underlying Chinese classic is the *Yi Jing* or *I Ching*, the Book of Changes, whose history and interpretive traditions are far richer than that occult comparison.",
      "This entry therefore does two jobs: it points toward the trigrams and hexagrams of the Yi Jing, and it preserves the old Western occult misreading as an example of how encyclopedias themselves become historical artefacts."
    ],
    "sections": [
      {
        "heading": "Lines that change",
        "paragraphs": [
          "The Yi Jing is organised around broken and unbroken lines combined into eight trigrams and sixty-four hexagrams. Consultation traditions use those figures together with a layered textual commentary concerned with change, situation and response.",
          "The binary visual language is striking enough that later Western occultists eagerly compared it with their own number diagrams."
        ]
      },
      {
        "heading": "The dragon-horse story in the old archive",
        "paragraphs": [
          "The 1920 encyclopedia retells an antiquarian story of a ruler receiving cosmic patterns from a strange horse-dragon marked with symbols. Such legendary material belongs to the reception history, but the dates and cross-cultural equations in older occult books should not be treated as modern scholarship.",
          "That tension is exactly why this entry sits deep in the archive: the subject is partly the book and partly the history of people misunderstanding the book."
        ]
      },
      {
        "heading": "Do not turn every grid into Kabbalah",
        "paragraphs": [
          "Lévi and other occult writers loved universal correspondences. Similar-looking diagrams were often presented as evidence that distant traditions shared one hidden source. Visual resemblance is interesting; it is not by itself proof of historical connection."
        ]
      }
    ],
    "related": [
      "Divination",
      "Magic Squares",
      "Kabala",
      "Geomancy"
    ],
    "catalogue": []
  },
  {
    "title": "Zodiac",
    "slug": "zodiac",
    "type": "Twelve-sign celestial framework in astrology",
    "depth": "Surface",
    "synopsis": "The familiar ring of twelve astrological signs, built from an ancient division of the Sun's apparent path through the sky rather than twelve identical constellations.",
    "lead": [
      "The zodiac is the belt of the sky around the ecliptic - the apparent yearly path of the Sun - divided in astrology into twelve signs. The sign names come from constellations along that route, but a zodiac sign and the modern astronomical boundaries of a constellation are not the same thing.",
      "That difference opens an immediate rabbit hole. Western tropical astrology anchors its signs to seasonal points such as the equinox, while sidereal systems tie the zodiac more closely to stellar positions. Same twelve names, different reference frame."
    ],
    "sections": [
      {
        "heading": "Twelve equal signs, unequal constellations",
        "paragraphs": [
          "Astrological signs divide the circle into twelve equal thirty-degree sections. Actual constellations occupy irregular areas of sky. Popular diagrams often merge the two because the symbolic animal or figure is easier to draw than an invisible thirty-degree sector.",
          "A good journal illustration can show both layers at once: measured circle underneath, wandering star pattern above."
        ]
      },
      {
        "heading": "A calendar written around the sky",
        "paragraphs": [
          "The zodiac became a framework for timing, personality, planetary interpretation and medical or magical correspondences. Once the twelve signs exist as a circular index, almost anything can be assigned around them: elements, body parts, colours, plants, stones and ritual hours.",
          "That capacity to organise correspondences is why zodiac wheels recur throughout occult design."
        ]
      },
      {
        "heading": "Horoscope versus zodiac",
        "paragraphs": [
          "The zodiac is the coordinate framework; a horoscope is a chart calculated for a particular moment and place. Sun-sign columns simplify that much larger chart tradition into one familiar sign, which is why 'what's your zodiac?' feels much simpler than actual astrology."
        ]
      }
    ],
    "related": [
      "Hermes Trismegistus",
      "Tarot",
      "Aeromancy",
      "Fibonacci Sequence",
      "Magic Squares"
    ],
    "catalogue": []
  },
  {
    "title": "Ars Notoria",
    "slug": "ars-notoria",
    "type": "Medieval ritual text for knowledge, memory & angelic revelation",
    "depth": "Abyss",
    "synopsis": "A medieval ritual text in which prayers, sacred names and diagrammatic notae were used in the hope of acquiring learning, memory and understanding.",
    "lead": [
      "Ars Notoria - the 'Notory Art' - sits in the strange overlap between medieval study culture, devotion and ritual magic. Surviving Latin texts use extended prayers, sacred names and visual figures called notae in a programme intended to obtain knowledge and sharpen mental faculties through divine or angelic aid.",
      "It is less a spellbook of quick tricks than a demanding curriculum. Grammar, rhetoric, philosophy, theology and other fields of learning appear as things to be approached through timed prayer, preparation and contemplation."
    ],
    "sections": [
      {
        "heading": "A grimoire for students",
        "paragraphs": [
          "The tradition emerged in the Middle Ages and circulated in multiple versions. Its promises make particular sense beside the growth of schools and universities: difficult bodies of knowledge are reimagined as something revelation might accelerate.",
          "That gives Ars Notoria a distinctive mood. The desired treasure is not gold or invisibility but understanding."
        ]
      },
      {
        "heading": "The notae are part of the point",
        "paragraphs": [
          "Some manuscripts preserve elaborate figures associated with fields of knowledge. They are not ordinary explanatory diagrams. Within the ritual system, looking at the figures belongs to a larger devotional sequence of words, timings and preparation.",
          "For the OPS archive, they are a useful reminder that an occult diagram can be both visual information and ritual object at the same time."
        ]
      },
      {
        "heading": "Solomonic, but not simply the Goetia",
        "paragraphs": [
          "Later manuscript culture often groups very different works under the authority of Solomon. Ars Notoria belongs in that broad Solomonic neighbourhood, but its emphasis on learning and angelic knowledge is quite different from the spirit catalogues most people now associate with the Lesser Key of Solomon."
        ]
      }
    ],
    "related": [
      "Lemegeton",
      "Ceremonial Magic",
      "Invocation",
      "Hermes Trismegistus"
    ],
    "catalogue": []
  },
  {
    "title": "Hand of Glory",
    "slug": "hand-of-glory",
    "type": "European thieves' charm & preserved-hand folklore",
    "depth": "Abyss",
    "synopsis": "A grisly European folklore object said to help thieves by holding a supernatural light, defeating locks or keeping a household asleep.",
    "lead": [
      "The Hand of Glory is one of those folklore objects that sounds invented for a gothic novel and then turns out to have a long paper trail. Stories describe a preserved hand of an executed criminal used by thieves, often as a holder for a strange candle or as the source of an uncanny light.",
      "The powers change from telling to telling: sleepers cannot wake, locks give way, flames behave impossibly. The object works because several fears are compressed into one image - the gallows, the dead body, burglary at night and a light that should not exist."
    ],
    "sections": [
      {
        "heading": "A North Yorkshire afterlife",
        "paragraphs": [
          "Whitby Museum holds a mummified hand found concealed in a cottage wall at Castleton and donated to the museum in the twentieth century. It is presented as the only alleged surviving Hand of Glory of its type, though there is no evidence that it was ever burned as some versions of the legend require.",
          "That local survival makes the story especially vivid in Britain: a continental thieves' charm also became part of northern English folklore."
        ]
      },
      {
        "heading": "The burglar's impossible lantern",
        "paragraphs": [
          "In story versions, the light is less about illumination than control. A house is made helpless while the intruder moves through it. Counter-charms are just as memorable as the charm itself, which is typical of practical folklore: every impossible object attracts an equally specific way to stop it."
        ]
      },
      {
        "heading": "A strange name beside the mandrake",
        "paragraphs": [
          "Older writers connected the phrase 'Hand of Glory' with French main de gloire and with mandragore, the mandrake. The etymological route is debated, but the association helped two already uncanny images - a human-shaped root and a dead hand - bleed into the same folklore shelf."
        ]
      }
    ],
    "related": [
      "Mandragoras",
      "Witchcraft",
      "Amulets",
      "Necromancy"
    ],
    "catalogue": []
  },
  {
    "title": "Sibylline Books",
    "slug": "sibylline-books",
    "type": "Roman state oracle collection",
    "depth": "Abyss",
    "synopsis": "A legendary and historical Roman collection of Greek oracular verses consulted by appointed priests during moments of public crisis.",
    "lead": [
      "The Sibylline Books belong to a very different world from the private fortune-teller's book. In Roman tradition they were a state-controlled collection of oracular verses, kept under official guardianship and consulted when prodigies, disasters or political emergencies demanded a religious response.",
      "The famous purchase story says a Sibyl offered books to King Tarquin, destroyed some when he refused the price, and finally sold the surviving portion for the original sum. Whatever the legend adds, Rome really did maintain collections known as the Sibylline Books."
    ],
    "sections": [
      {
        "heading": "Not a timetable of future events",
        "paragraphs": [
          "Ancient accounts suggest the books were consulted less as a list of precise predictions than as a way to determine rites, dedications or other actions appropriate to a crisis. The answer was therefore often 'what must Rome do?' rather than 'what will happen next?' "
        ]
      },
      {
        "heading": "Destroyed and reconstructed",
        "paragraphs": [
          "The collection kept on the Capitoline was lost when the temple of Jupiter burned in 83 BCE. Roman authorities later gathered replacement oracles from other locations and subjected them to official review, so 'the Sibylline Books' refers to a tradition that changed materially over time."
        ]
      },
      {
        "heading": "The people behind the locked shelf",
        "paragraphs": [
          "Access belonged to a priestly college eventually known as the quindecemviri sacris faciundis - fifteen men responsible for the sacred rites. That turns the books into both an oracle and an institution: mysterious words mattered because a controlled body decided when and how to read them."
        ]
      }
    ],
    "related": [
      "Quindecem Viri",
      "Divination",
      "Zodiac",
      "Ceremonial Magic"
    ],
    "catalogue": []
  },
  {
    "title": "Quindecem Viri",
    "slug": "quindecem-viri",
    "type": "Older archive wording for Rome's fifteen-man Sibylline priesthood",
    "depth": "Abyss",
    "synopsis": "An old index trail leading to the quindecemviri sacris faciundis, the Roman college of fifteen priests charged with guarding and consulting the Sibylline Books.",
    "lead": [
      "'Quindecem Viri' looks like an obscure occult name because old indexes split the Latin into two words. The rabbit hole leads instead to a real Roman priestly college: the quindecemviri sacris faciundis, literally the fifteen men for carrying out sacred rites.",
      "Their presence in an occult encyclopedia is a useful lesson in how archives work. A strange-looking term may not name a magical technique at all; sometimes it is a doorway into the historical machinery around divination."
    ],
    "sections": [
      {
        "heading": "Guardians rather than freelance diviners",
        "paragraphs": [
          "The college had official religious duties, most famously care of the Sibylline Books. Consultation happened in response to public events and under Roman authority, not as private fortune telling."
        ]
      },
      {
        "heading": "Why the number changed",
        "paragraphs": [
          "Earlier custodial bodies were remembered as smaller colleges before the number expanded. The later title fixes the membership at fifteen, which is the form most closely associated with the late Republican and Imperial Roman institution."
        ]
      },
      {
        "heading": "A tiny index entry with a large context",
        "paragraphs": [
          "This is exactly the sort of term that belongs at the bottom of the iceberg. On its own it is nearly opaque. Connected back to the Sibylline Books, Roman prodigy lore and official ritual, it suddenly explains who was allowed to open one of antiquity's most famous locked collections."
        ]
      }
    ],
    "related": [
      "Sibylline Books",
      "Divination",
      "Ceremonial Magic",
      "Zodiac"
    ],
    "catalogue": []
  },
  {
    "title": "Cacodaemons",
    "slug": "cacodaemons",
    "type": "Old term for harmful or ill-omened spirits",
    "depth": "Abyss",
    "synopsis": "An old demonological and astrological word built from Greek roots for a bad or harmful daimon, later flattened into the idea of an evil spirit.",
    "lead": [
      "Cacodaemon - also written cacodemon - comes from Greek language around a bad or harmful daimon. In later English occult and demonological writing it becomes a compact label for an evil spirit or malign supernatural influence.",
      "The word is useful because 'daimon' did not begin as a simple synonym for the later Christian demon. Translation and religious change gradually push a wider category of spirit toward a much narrower moral meaning."
    ],
    "sections": [
      {
        "heading": "Bad daimon, bad fortune",
        "paragraphs": [
          "The Greek roots carry the sense of an adverse or harmful spiritual agency. Early-modern writers then inherit the word through a culture increasingly comfortable translating unfamiliar spirits as devils or demons."
        ]
      },
      {
        "heading": "The twelfth house connection",
        "paragraphs": [
          "Older astrological vocabulary also used Cacodemon as a name for the twelfth house, traditionally associated with difficulty and misfortune. That is a different use of the same ominous word, showing how astrology and spirit-language could share a dictionary."
        ]
      },
      {
        "heading": "Do not make every spirit the same creature",
        "paragraphs": [
          "Jinn, daimones, familiars, demons and folkloric spirits come from different languages and traditions. Old occult reference books often line them up as though they were interchangeable. The more useful modern reading keeps the historical distinctions visible."
        ]
      }
    ],
    "related": [
      "Jinn",
      "Familiars",
      "Exorcism",
      "Invocation",
      "Baphomet"
    ],
    "catalogue": []
  },
  {
    "title": "Mandragoras",
    "slug": "mandragoras",
    "type": "Mandrake plant folklore under an old archive name",
    "depth": "Abyss",
    "synopsis": "An old index form of mandrake lore: a real medicinal plant whose human-shaped roots accumulated stories of screams, gallows, charms and dangerous harvesting.",
    "lead": [
      "Mandragoras is the old archive trail into mandrake lore. Mandragora is a real genus of plants with a long medicinal history, but its forked roots are suggestive enough that centuries of storytellers repeatedly saw little bodies in them.",
      "From that resemblance came an extraordinary second life: roots that scream when pulled, plants gathered with elaborate precautions, charms shaped like people and stories linking the mandrake to gallows ground."
    ],
    "sections": [
      {
        "heading": "A real plant beneath the legend",
        "paragraphs": [
          "Mandrake species contain biologically active tropane alkaloids. Historical medicinal and narcotic effects sit underneath some of the plant's reputation, but folklore expands far beyond pharmacology into spirit, luck, fertility and protection."
        ]
      },
      {
        "heading": "The root that looks back",
        "paragraphs": [
          "Human resemblance matters. A root that forks like legs or sprouts side roots like arms invites the doctrine-of-signatures style of thinking in which shape suggests hidden power. Carved and dressed roots push that resemblance even further."
        ]
      },
      {
        "heading": "Gallows, dogs and the impossible harvest",
        "paragraphs": [
          "European legends surround the act of pulling the plant with elaborate danger. Dogs, trumpets, circles and warnings of a deadly scream appear in different versions. These are folklore motifs rather than instructions, but visually they made mandrake one of the most durable plants in occult art."
        ]
      }
    ],
    "related": [
      "Hand of Glory",
      "Amulets",
      "Witchcraft",
      "Aetites"
    ],
    "catalogue": []
  },
  {
    "title": "Bibliomancy",
    "slug": "bibliomancy",
    "type": "Divination by opening and interpreting a book",
    "depth": "Deep Water",
    "synopsis": "A form of divination in which a book is opened or a passage selected by chance and the encountered words are read as an answer, omen or piece of guidance.",
    "lead": [
      "Bibliomancy turns the book itself into the chance mechanism. A question is held in mind, a book is opened without deliberately choosing the passage, and whatever line appears is interpreted as meaningful.",
      "Sacred texts have often been favoured, but the broader family includes classical and literary sortes traditions too. The uncanny part is simple: a text written for one purpose suddenly seems to answer a completely different question."
    ],
    "sections": [
      {
        "heading": "Chance chooses the line",
        "paragraphs": [
          "The method belongs to the same large family as lots, cards and other divination systems that deliberately introduce an uncontrolled selection step. Interpretation comes after the random opening rather than before it."
        ]
      },
      {
        "heading": "Sortes and famous books",
        "paragraphs": [
          "Greek and Roman traditions included forms based on Homer or Virgil, while later Christian practice frequently used the Bible. Similar habits appear with other culturally authoritative books, which is why bibliomancy is better understood as a pattern than as one fixed ritual."
        ]
      },
      {
        "heading": "The archive can do this without pretending",
        "paragraphs": [
          "As a modern playful device, a random archive entry can reproduce the literary pleasure of bibliomancy without claiming supernatural certainty: open a strange page, notice what catches, then follow the links."
        ]
      }
    ],
    "related": [
      "Divination",
      "Y-Kim (Book of)",
      "Tarot",
      "Apantomancy"
    ],
    "catalogue": []
  },
  {
    "title": "Crystalomancy",
    "slug": "crystalomancy",
    "type": "Crystal gazing & reflective-surface divination",
    "depth": "Deep Water",
    "synopsis": "An older name for crystal gazing: using a crystal, glass or similarly reflective focus for scrying, visions or symbolic impressions.",
    "lead": [
      "Crystalomancy is the old reference-book word for crystal gazing. The familiar crystal ball is only one version of a wider scrying idea: attention is fixed on a reflective, translucent or visually ambiguous surface until shapes, images or impressions are interpreted.",
      "The object does not need to contain a literal picture. Much of the experience comes from sustained looking, expectation and the mind's habit of finding forms in uncertain visual information."
    ],
    "sections": [
      {
        "heading": "Why a crystal works so well as an image",
        "paragraphs": [
          "Polished quartz and glass can hold reflections, distortions, internal flaws and moving highlights at the same time. That makes them perfect visual theatres for a practice built around ambiguous seeing."
        ]
      },
      {
        "heading": "Scrying is the broader shelf",
        "paragraphs": [
          "Mirrors, water, dark bowls and other surfaces can play similar roles. 'Crystalomancy' names the crystal-centred branch; 'scrying' is the broader modern umbrella people are more likely to recognise."
        ]
      },
      {
        "heading": "From parlour image to occult icon",
        "paragraphs": [
          "The crystal ball became such a strong visual shorthand for fortune telling that it now appears far beyond actual historical practice - in stage magic, cartoons, shop signs and costume. That makes it useful to separate the icon from the much messier history behind it."
        ]
      }
    ],
    "related": [
      "Divination",
      "Aeromancy",
      "Geomancy",
      "Zodiac"
    ],
    "catalogue": []
  }
];
  const existing=new Set((pack.entries||[]).map(x=>String(x.title||'').toLowerCase()));
  incoming.forEach(x=>{ if(!existing.has(String(x.title||'').toLowerCase())) pack.entries.push(x); });
  const patches={
  "Abracadabra": [
    "Magic Squares",
    "Pentagram"
  ],
  "Abraxas": [
    "Baphomet",
    "Seal of Solomon"
  ],
  "Aeromancy": [
    "Divination",
    "Zodiac"
  ],
  "Dactylomancy": [
    "Divination",
    "Geomancy"
  ],
  "Aetites": [
    "Quirinus",
    "Amulets"
  ],
  "Adjuration": [
    "Invocation",
    "Exorcism"
  ],
  "Abaris": [
    "Hermes Trismegistus",
    "Divination"
  ],
  "Alectryomancy": [
    "Divination",
    "Familiars"
  ],
  "Aleuromancy": [
    "Divination",
    "Geomancy"
  ],
  "Amniomancy": [
    "Divination",
    "Witchcraft"
  ],
  "Amulets": [
    "Seal of Solomon",
    "Obeah"
  ],
  "Apantomancy": [
    "Divination",
    "Xylomancy"
  ]
};
  (pack.entries||[]).forEach(entry=>{ const add=patches[entry.title]||[]; if(add.length){ entry.related=[...new Set([...(entry.related||[]),...add])]; } });
  pack.version='22.3.57';
  window.OPS_OCCULT_TAXONOMY={
  "version": "22.3.57",
  "depths": [
    {
      "name": "Surface",
      "description": "Familiar names, recognisable symbols and broad ideas that make an easy doorway into the archive."
    },
    {
      "name": "Shallows",
      "description": "More specific traditions, texts and practices that appear once the familiar words start splitting into historical branches."
    },
    {
      "name": "Deep Water",
      "description": "Specialist divination methods, ritual vocabulary and difficult source traditions that reward a slower read."
    },
    {
      "name": "Abyss",
      "description": "Rare, fragmentary and unusually obscure material - the shelf where one strange line in an old index can become a whole new rabbit hole."
    }
  ],
  "topics": [
    {
      "name": "Divination & Omens",
      "kicker": "WAYS OF READING SIGNS",
      "description": "Cards, stars, weather, wood, rings, lots, chance meetings and structured systems for asking uncertainty to take a shape.",
      "entries": [
        "Divination",
        "Tarot",
        "Geomancy",
        "Aeromancy",
        "Dactylomancy",
        "Alectryomancy",
        "Aleuromancy",
        "Amniomancy",
        "Apantomancy",
        "Xylomancy",
        "Urim and Thummim"
      ]
    },
    {
      "name": "Symbols, Seals & Hidden Geometry",
      "kicker": "LINES THAT CARRY MORE THAN ONE MEANING",
      "description": "Pentagrams, seals, number grids, traced names and mathematical scaffolds that turn geometry into symbolic language.",
      "entries": [
        "Pentagram",
        "Seal of Solomon",
        "Magic Squares",
        "Fibonacci Sequence",
        "Abracadabra",
        "Abraxas"
      ]
    },
    {
      "name": "Amulets, Talismans & Protective Things",
      "kicker": "THINGS CARRIED FOR A REASON",
      "description": "Objects worn, hidden or inscribed because somebody believed the material itself could guard, heal, attract luck or hold a power.",
      "entries": [
        "Amulets",
        "Aetites",
        "Seal of Solomon",
        "Abracadabra",
        "Abraxas",
        "Urim and Thummim"
      ]
    },
    {
      "name": "Books, Grimoires & Manuscripts",
      "kicker": "THE SHELVES GET STRANGER",
      "description": "Texts that became magical objects in their own right, from grimoires and Hermetic writings to old occult reinterpretations of much older books.",
      "entries": [
        "Lemegeton",
        "Ceremonial Magic",
        "Hermes Trismegistus",
        "Kabala",
        "Y-Kim (Book of)",
        "Tarot"
      ]
    },
    {
      "name": "Ritual, Invocation & Exorcism",
      "kicker": "WORDS, BOUNDARIES & CEREMONY",
      "description": "Formal ritual language used to call, command, dismiss, protect or mark a boundary between ordinary space and ritual space.",
      "entries": [
        "Ceremonial Magic",
        "Invocation",
        "Exorcism",
        "Adjuration",
        "Lemegeton",
        "Necromancy"
      ]
    },
    {
      "name": "Spirits, Revenants & Strange Beings",
      "kicker": "WHO OR WHAT IS ON THE OTHER SIDE?",
      "description": "Jinn, familiars, vampires and symbolic beings whose identities change dramatically between religion, folklore, accusation and art.",
      "entries": [
        "Jinn",
        "Familiars",
        "Vampire",
        "Baphomet",
        "Abraxas"
      ]
    },
    {
      "name": "Witchcraft, Folk Magic & Contested Traditions",
      "kicker": "THE ARCHIVE NEEDS CONTEXT",
      "description": "Terms shaped by living practice, accusation, colonial description and later reinvention, where the person doing the naming matters.",
      "entries": [
        "Witchcraft",
        "Obeah",
        "Familiars",
        "Amulets",
        "Exorcism"
      ]
    },
    {
      "name": "Numbers, Stars & Correspondences",
      "kicker": "THE WORLD ARRANGED AS A DIAGRAM",
      "description": "Mathematical and celestial systems that become frameworks for proportion, timing, symbolism and occult correspondence.",
      "entries": [
        "Fibonacci Sequence",
        "Magic Squares",
        "Zodiac",
        "Pentagram",
        "Hermes Trismegistus",
        "Kabala"
      ]
    },
    {
      "name": "Stones, Objects & Curiosities",
      "kicker": "THE OCCULT CABINET",
      "description": "Engraved gems, legendary stones and tiny impossible objects that sit halfway between natural history, folklore and talismanic belief.",
      "entries": [
        "Aetites",
        "Quirinus",
        "Abraxas",
        "Amulets",
        "Urim and Thummim"
      ]
    },
    {
      "name": "People, Orders & Legendary Authorities",
      "kicker": "FIGURES IN THE MARGINS",
      "description": "Sages, secret brotherhoods and legendary authorities whose names became containers for books, symbols and entire traditions.",
      "entries": [
        "Hermes Trismegistus",
        "Rosicrucians",
        "Abaris",
        "Lemegeton"
      ]
    }
  ]
};
  const ops57TopicAdds={
    "Divination & Omens":["Bibliomancy","Crystalomancy","Sibylline Books"],
    "Grimoires, Books & Manuscripts":["Ars Notoria","Sibylline Books"],
    "Spirits, Revenants & Strange Beings":["Cacodaemons"],
    "Stones, Objects & Curiosities":["Hand of Glory","Mandragoras"],
    "People, Orders & Legendary Authorities":["Quindecem Viri"]
  };
  (window.OPS_OCCULT_TAXONOMY.topics||[]).forEach(t=>{ const a=ops57TopicAdds[t.name]||[]; if(a.length)t.entries=[...new Set([...(t.entries||[]),...a])]; });

})();
